import React, { useEffect, useState } from "react";
import { Plus, Edit, MoreHorizontal, Trash2, ArrowRightLeft, LogOut as DischargeIcon, Power, UserPlus, Clock, Video } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import DataTable from "@/components/common/DataTable";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import HotlineStatusBadge from "@/pages/hotline/HotlineStatusBadge";
import { useNavigate as useNavigateHook } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";

/*
 * R-015 — mirrors StorePatientRequest / PatientResource exactly.
 *
 * Dropped, because the API has no such column and `validate()` silently discards
 * unknown keys (so these saved as nothing, with no error):
 *   mrn              — server-generated atomically via MrnSequence, never sent
 *   state, zip_code  — not on the patients table
 *   admission_date   — not on the patients table
 *   notes            — only discharge_notes exists, set by the discharge flow
 *   status           — lifecycle moves via activate/deactivate, not the form
 * Renamed: primary_diagnosis -> diagnosis.
 */
const EMPTY = {
  first_name: "", last_name: "", first_name_ar: "", last_name_ar: "",
  national_id: "", dob: "", gender: "", phone: "", email: "",
  address: "", city: "", nationality: "", blood_type: "", primary_language: "",
  facility_id: "", diagnosis: "",
};

export default function PatientsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [facilityFilter, setFacilityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [tab, setTab] = useState("profile");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [timeline, setTimeline] = useState([]);

  const [transfer, setTransfer] = useState({ open: false, patient: null, facility_id: "" });
  const [confirm, setConfirm] = useState({ open: false });

  async function load() {
    setLoading(true);
    try {
      // R-015 — Laravel names: per_page / sort / direction. The old page_size /
      // sort_by / sort_dir were dropped by validate(), so paging and sorting
      // silently did nothing.
      const params = { page, per_page: pageSize, sort: sortBy, direction: sortDir < 0 ? "desc" : "asc" };
      if (search) params.q = search;
      if (statusFilter !== "all") params.status = statusFilter;
      if (facilityFilter !== "all") params.facility_id = facilityFilter;
      const res = await api.get("/patients", { params });
      setItems(res.data.data ?? []);
      setTotal(res.data.meta?.total ?? 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, sortBy, sortDir, statusFilter, facilityFilter]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  useEffect(() => {
    api.get("/facilities", { params: { per_page: 50 } }).then((r) => setFacilities(r.data.data ?? [])).catch(() => {});
  }, []);

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY });
    setErrors({});
    setTab("profile");
    setSheetOpen(true);
  }
  async function openEdit(row) {
    setEditing(row);
    setForm({
      first_name: row.first_name, last_name: row.last_name,
      first_name_ar: row.first_name_ar || "", last_name_ar: row.last_name_ar || "",
      national_id: row.national_id || "", dob: row.dob || "", gender: row.gender || "",
      phone: row.phone || "", email: row.email || "",
      address: row.address || "", city: row.city || "",
      nationality: row.nationality || "", blood_type: row.blood_type || "",
      primary_language: row.primary_language || "",
      facility_id: row.facility_id ? String(row.facility_id) : "",
      diagnosis: row.diagnosis || "",
    });
    setErrors({});
    setTab("profile");
    setSheetOpen(true);
    try {
      const tl = await api.get(`/patients/${row.id}/timeline`);
      // Stubbed server-side until audit_trail lands (PatientController@timeline).
      setTimeline(tl.data.data || []);
    } catch (_e) { setTimeline([]); }
  }

  function validate() {
    const e = {};
    if (!form.first_name) e.first_name = "First name is required";
    if (!form.last_name) e.last_name = "Last name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function save() {
    if (!validate()) return;
    setSaving(true);
    try {
      // Blank optional inputs are omitted rather than sent as "" — `national_id`
      // carries a regex and `email`/`dob` their own format rules, all of which
      // reject an empty string.
      const p = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "" && v !== null));
      if (editing) {
        await api.put(`/patients/${editing.id}`, p);
        toast.success("Patient updated");
      } else {
        await api.post("/patients", p);
        toast.success("Patient created");
      }
      setSheetOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function doAction(row, action) {
    try {
      if (action === "delete") {
        await api.delete(`/patients/${row.id}`);
        toast.success("Patient archived");
      } else {
        await api.post(`/patients/${row.id}/${action}`);
        toast.success(`Patient ${action}d`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function doTransfer() {
    try {
      await api.post(`/patients/${transfer.patient.id}/transfer`, null, { params: { facility_id: transfer.facility_id } });
      toast.success("Patient transferred");
      setTransfer({ open: false, patient: null, facility_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const facilityName = (id) => facilities.find((f) => f.id === id)?.name || "—";

  const columns = [
    {
      key: "name", label: "Patient",
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-[#1F2937] truncate">{r.first_name} {r.last_name}</div>
          <div className="text-xs text-[#6B7280]">{r.mrn} · {r.gender || "—"} · DOB {r.dob || "—"}</div>
        </div>
      ),
    },
    { key: "primary_diagnosis", label: "Primary diagnosis", sortable: false, render: (r) => r.primary_diagnosis || "—" },
    { key: "facility_id", label: "Facility", render: (r) => <span className="text-sm text-[#1F2937]">{facilityName(r.facility_id)}</span> },
    { key: "admission_date", label: "Admitted", sortable: true, render: (r) => r.admission_date || "—" },
    { key: "status", label: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    {
      key: "actions", label: "",
      render: (r) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`patient-actions-${r.id}`} onClick={(e) => e.stopPropagation()} className="w-8 h-8 rounded-md hover:bg-[#F5F7FA] flex items-center justify-center">
              <MoreHorizontal className="w-4 h-4 text-[#6B7280]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`patient-edit-${r.id}`}>
              <Edit className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTransfer({ open: true, patient: r, facility_id: "" })}>
              <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setConfirm({
                open: true, title: "Discharge patient",
                description: `Mark ${r.first_name} ${r.last_name} as discharged?`,
                onConfirm: () => doAction(r, "discharge"),
              })}
            >
              <DischargeIcon className="w-4 h-4 mr-2" /> Discharge
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {r.status !== "active" ? (
              <DropdownMenuItem onClick={() => doAction(r, "activate")}><Power className="w-4 h-4 mr-2" /> Activate</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => doAction(r, "deactivate")}><Power className="w-4 h-4 mr-2" /> Deactivate</DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-[#EF4444]"
              data-testid={`patient-delete-${r.id}`}
              onClick={() => setConfirm({
                open: true, title: "Archive patient", description: `Archive ${r.first_name} ${r.last_name}?`,
                onConfirm: () => doAction(r, "delete"), destructive: true,
              })}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Archive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div data-testid="patients-page">
      <PageHeader
        title="Patient Management"
        subtitle="Census, care team assignment, and patient lifecycle across the network."
        actions={
          <Button onClick={openCreate} data-testid="create-patient-button" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
            <UserPlus className="w-4 h-4 mr-1.5" /> Admit Patient
          </Button>
        }
      />

      <DataTable
        testId="patients-table"
        columns={columns}
        data={items}
        total={total}
        page={page}
        pageSize={pageSize}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        onPageChange={setPage}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        sortBy={sortBy} sortDir={sortDir}
        exportFilename="patients.csv"
        filters={
          <>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px] h-9 border-[#E5E7EB]" data-testid="patient-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="discharged">Discharged</SelectItem>
                <SelectItem value="transferred">Transferred</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={facilityFilter} onValueChange={(v) => { setFacilityFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[200px] h-9 border-[#E5E7EB]" data-testid="patient-facility-filter">
                <SelectValue placeholder="Facility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All facilities</SelectItem>
                {facilities.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        }
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[720px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>{editing ? `${editing.first_name} ${editing.last_name}` : "Admit patient"}</SheetTitle>
            <SheetDescription>{editing ? `MRN ${editing.mrn}` : "Capture admission details for a new patient."}</SheetDescription>
          </SheetHeader>

          {editing ? (
            <Tabs value={tab} onValueChange={setTab} className="p-6">
              <TabsList className="bg-[#F5F7FA]">
                <TabsTrigger value="profile" data-testid="patient-tab-profile">Profile</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="patient-tab-timeline">Timeline</TabsTrigger>
                <TabsTrigger value="hotline" data-testid="patient-tab-hotline">Nurse Hotline</TabsTrigger>
                <TabsTrigger value="teleconsult" data-testid="patient-tab-teleconsult">Teleconsult</TabsTrigger>
                <TabsTrigger value="audit" data-testid="patient-tab-audit">Audit</TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="mt-4 space-y-4">
                <PatientFormFields form={form} setForm={setForm} errors={errors} facilities={facilities} mrn={editing?.mrn} />
              </TabsContent>

              <TabsContent value="timeline" className="mt-4">
                <div className="hah-card p-5">
                  <h3 className="text-base font-semibold text-[#1F2937] mb-4">Patient timeline</h3>
                  <ol className="relative border-l border-[#E5E7EB] ml-3 space-y-5">
                    {timeline.length === 0 && <li className="text-sm text-[#6B7280] pl-4">No events yet.</li>}
                    {timeline.map((t) => (
                      <li key={t.id} className="ml-4">
                        <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-[#0B7E8C] ring-4 ring-white" />
                        <div className="flex items-center gap-2">
                          <StatusBadge status={t.action.toLowerCase()} label={t.action} />
                          <span className="text-xs text-[#6B7280]">{new Date(t.created_at).toLocaleString()}</span>
                        </div>
                        <div className="text-sm text-[#1F2937] mt-1">{t.user_email || "system"}</div>
                      </li>
                    ))}
                  </ol>
                </div>
              </TabsContent>

              <TabsContent value="hotline" className="mt-4">
                <PatientHotlineTab patientId={editing.id} />
              </TabsContent>

              <TabsContent value="teleconsult" className="mt-4">
                <PatientTeleconsultTab patientId={editing.id} />
              </TabsContent>

              <TabsContent value="audit" className="mt-4">
                <div className="hah-card p-5">
                  <h3 className="text-base font-semibold text-[#1F2937] mb-3">Audit history</h3>
                  {timeline.length === 0 ? <div className="text-sm text-[#6B7280]">No audit events.</div> :
                    <ul className="space-y-2 text-sm">
                      {timeline.map((t) => (
                        <li key={t.id} className="flex items-center justify-between border-b border-[#E5E7EB] pb-2">
                          <span className="flex items-center gap-2"><Clock className="w-3 h-3 text-[#94A3B8]" /> {new Date(t.created_at).toLocaleString()}</span>
                          <span className="text-[#6B7280]">{t.user_email}</span>
                          <StatusBadge status={t.action.toLowerCase()} label={t.action} />
                        </li>
                      ))}
                    </ul>
                  }
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="p-6 space-y-4">
              <PatientFormFields form={form} setForm={setForm} errors={errors} facilities={facilities} />
            </div>
          )}

          <StickyActionBar>
            <Button variant="outline" onClick={() => setSheetOpen(false)} className="border-[#E5E7EB]" data-testid="patient-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="patient-form-save" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
              {saving ? "Saving…" : editing ? "Save changes" : "Admit patient"}
            </Button>
          </StickyActionBar>
        </SheetContent>
      </Sheet>

      {/* Transfer dialog */}
      <Sheet open={transfer.open} onOpenChange={(o) => setTransfer({ ...transfer, open: o })}>
        <SheetContent side="right" className="w-full sm:max-w-[420px]">
          <SheetHeader>
            <SheetTitle>Transfer patient</SheetTitle>
            <SheetDescription>
              Move {transfer.patient?.first_name} {transfer.patient?.last_name} to a different facility.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-3">
            <Select value={transfer.facility_id} onValueChange={(v) => setTransfer({ ...transfer, facility_id: v })}>
              <SelectTrigger data-testid="transfer-facility-select"><SelectValue placeholder="Select destination facility" /></SelectTrigger>
              <SelectContent>{facilities.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="transfer-confirm" disabled={!transfer.facility_id} onClick={doTransfer} className="w-full bg-[#0B7E8C] hover:bg-[#128C97] text-white">Confirm transfer</Button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title}
        description={confirm.description}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }}
        destructive={confirm.destructive}
      />
    </div>
  );
}

function PatientFormFields({ form, setForm, errors, facilities, mrn }) {
  return (
    <>
      <FormSection title="General Information" testId="patient-form-general" description="Patient identity and demographics.">
        {/* R-015 — MRN is allocated atomically by the API (MrnSequence) and shown
            read-only once the record exists; there is no input for it. Status moved
            out too: it changes through activate/deactivate/discharge/transfer, and
            the PUT never accepted it. */}
        {mrn && (
          <FormField label="MRN">
            <Input data-testid="patient-mrn" value={mrn} disabled readOnly />
          </FormField>
        )}
        <FormField label="First name" required error={errors.first_name}>
          <Input data-testid="patient-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        </FormField>
        <FormField label="Last name" required error={errors.last_name}>
          <Input data-testid="patient-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        </FormField>
        <FormField label="First name (Arabic)" error={errors.first_name_ar}>
          <Input dir="rtl" value={form.first_name_ar} onChange={(e) => setForm({ ...form, first_name_ar: e.target.value })} />
        </FormField>
        <FormField label="Last name (Arabic)" error={errors.last_name_ar}>
          <Input dir="rtl" value={form.last_name_ar} onChange={(e) => setForm({ ...form, last_name_ar: e.target.value })} />
        </FormField>
        <FormField label="National ID / Iqama" error={errors.national_id}>
          <Input inputMode="numeric" maxLength={10} placeholder="10 digits"
            value={form.national_id} onChange={(e) => setForm({ ...form, national_id: e.target.value })} />
        </FormField>
        <FormField label="Date of birth">
          <Input data-testid="patient-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
        </FormField>
        <FormField label="Gender">
          <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {/* R-015 — StorePatientRequest allows male|female only; the extra
                  options 422'd on save. */}
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Phone">
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </FormField>
        <FormField label="Email">
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </FormField>
        <FormField label="Address" full>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </FormField>
        <FormField label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></FormField>
        <FormField label="Nationality"><Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></FormField>
        <FormField label="Blood type"><Input maxLength={8} placeholder="e.g. O+" value={form.blood_type} onChange={(e) => setForm({ ...form, blood_type: e.target.value })} /></FormField>
        <FormField label="Primary language"><Input value={form.primary_language} onChange={(e) => setForm({ ...form, primary_language: e.target.value })} /></FormField>
      </FormSection>

      <FormSection title="Assignment" testId="patient-form-assignment" description="Facility and admission information.">
        <FormField label="Facility">
          <Select value={form.facility_id} onValueChange={(v) => setForm({ ...form, facility_id: v })}>
            <SelectTrigger data-testid="patient-facility-select"><SelectValue placeholder="Select facility" /></SelectTrigger>
            <SelectContent>{facilities.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.name}</SelectItem>)}</SelectContent>
          </Select>
        </FormField>
        {/* R-015 — no admission_date column; the API field is `diagnosis`. The
            free-text Notes section is gone with it (only discharge_notes exists,
            and the discharge flow owns that). */}
        <FormField label="Diagnosis" full>
          <Textarea rows={3} value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} />
        </FormField>
      </FormSection>
    </>
  );
}

function PatientHotlineTab({ patientId }) {
  const [data, setData] = React.useState({ sessions: [], escalations: [], follow_ups: [] });
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigateHook();
  React.useEffect(() => {
    api.get(`/hotline/patients/${patientId}/sessions`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [patientId]);
  return (
    <div className="space-y-4" data-testid="patient-hotline-tab">
      <div className="hah-card p-5">
        <h3 className="text-base font-semibold text-[#1F2937] mb-3">Hotline sessions</h3>
        {loading ? <div className="text-sm text-[#6B7280]">Loading…</div> : data.sessions.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No hotline sessions for this patient.</div>
        ) : (
          <div className="space-y-2">
            {data.sessions.map((s) => (
              <button key={s.id} onClick={() => navigate(`/hotline/sessions/${s.id}`)} className="w-full text-left p-3 border border-[#E5E7EB] rounded-lg hover:bg-[#EAF8FA]/40 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-[#0B7E8C] font-medium">{s.record_id}</span>
                    <span className="text-xs text-[#6B7280]">{new Date(s.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-[#1F2937] truncate">{s.chief_complaint?.description || "—"}</div>
                </div>
                <HotlineStatusBadge status={s.status} />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="hah-card p-5">
          <h4 className="font-semibold text-[#1F2937] mb-2">Escalations</h4>
          {data.escalations.length === 0 ? <div className="text-sm text-[#6B7280]">None.</div> : (
            <ul className="space-y-2 text-sm">
              {data.escalations.map((e) => (
                <li key={e.id} className="border-b border-[#E5E7EB] pb-1.5">
                  <div className="font-medium text-[#1F2937] capitalize">{e.type.replace(/_/g, " ")}</div>
                  <div className="text-xs text-[#6B7280]">{e.reason} · <span className="capitalize">{e.status.replace(/_/g, " ")}</span></div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="hah-card p-5">
          <h4 className="font-semibold text-[#1F2937] mb-2">Follow-ups</h4>
          {data.follow_ups.length === 0 ? <div className="text-sm text-[#6B7280]">None.</div> : (
            <ul className="space-y-2 text-sm">
              {data.follow_ups.map((f) => (
                <li key={f.id} className="border-b border-[#E5E7EB] pb-1.5">
                  <div className="font-medium text-[#1F2937]">Due {f.due_date}</div>
                  <div className="text-xs text-[#6B7280]">{f.reason} · <span className="capitalize">{f.status.replace(/_/g, " ")}</span></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}


const TC_STATUS_BADGES = {
  scheduled: { bg: "#DBEAFE", text: "#1E40AF", label: "Scheduled" },
  awaiting_participants: { bg: "#FEF3C7", text: "#92400E", label: "Awaiting" },
  in_progress: { bg: "#D1FAE5", text: "#065F46", label: "In Progress" },
  completed: { bg: "#F1F5F9", text: "#475569", label: "Completed" },
  cancelled: { bg: "#FEE2E2", text: "#991B1B", label: "Cancelled" },
  no_show: { bg: "#FED7AA", text: "#9A3412", label: "No Show" },
};

function PatientTeleconsultTab({ patientId }) {
  const [consults, setConsults] = React.useState([]);
  const [encounters, setEncounters] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const navigate = useNavigateHook();

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/teleconsult/consultations", { params: { patient_id: patientId, per_page: 50 } }),
      api.get("/teleconsult/encounters", { params: { patient_id: patientId } }),
    ])
      .then(([c, e]) => { setConsults(c.data.data ?? []); setEncounters(e.data.data ?? []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [patientId]);
  React.useEffect(() => { load(); }, [load]);

  async function startNow() {
    setCreating(true);
    try {
      const r = await api.post("/teleconsult/consultations", {
        patient_id: patientId, consultation_type: "follow_up",
        priority: "routine", duration_minutes: 30, source_module: "manual",
        notes: "Initiated from patient profile.",
      });
      toast.success("Consultation created");
      navigate(`/teleconsult/room/${r.data.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  }

  return (
    <div className="space-y-4" data-testid="patient-teleconsult-tab">
      <div className="hah-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[#1F2937]">Teleconsultations</h3>
          <Button size="sm" onClick={startNow} disabled={creating}
            className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="patient-tc-start">
            <Video className="w-3.5 h-3.5 mr-1.5" /> Start Teleconsult
          </Button>
        </div>
        {loading ? <div className="text-sm text-[#6B7280]">Loading…</div> : consults.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No teleconsultations yet.</div>
        ) : (
          <div className="space-y-2">
            {consults.map((c) => {
              const b = TC_STATUS_BADGES[c.status] || TC_STATUS_BADGES.scheduled;
              return (
                <button key={c.id} onClick={() => navigate(`/teleconsult/room/${c.id}`)}
                  className="w-full text-left p-3 border border-[#E5E7EB] rounded-lg hover:bg-[#EAF8FA]/40 flex items-center gap-3"
                  data-testid={`patient-tc-row-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#1F2937] capitalize">{(c.consultation_type || "").replace(/_/g, " ")}</span>
                      <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">{c.source_module || "manual"}</span>
                    </div>
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      {c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "Unscheduled"}
                      {c.provider_name ? ` · ${c.provider_name}` : ""}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
                    style={{ background: b.bg, color: b.text }}>{b.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="hah-card p-5">
        <h4 className="font-semibold text-[#1F2937] mb-2">Recent encounters</h4>
        {encounters.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No encounters recorded.</div>
        ) : (
          <ul className="space-y-3 text-sm">
            {encounters.slice(0, 8).map((e) => (
              <li key={e.id} className="border-b border-[#E5E7EB] pb-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#1F2937] capitalize">{(e.consultation_type || "").replace(/_/g, " ")}</span>
                  <span className="text-xs text-[#6B7280]">{e.ended_at ? new Date(e.ended_at).toLocaleString() : "—"} · {e.duration_minutes ?? 0}min</span>
                </div>
                {e.assessment && <div className="text-xs text-[#6B7280] mt-1 line-clamp-2">{e.assessment}</div>}
                {e.diagnosis && <div className="text-xs text-[#0B7E8C] mt-0.5">Dx: {e.diagnosis}</div>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

